import { useMutation, useQueryClient } from '@tanstack/react-query';
import LeadService, {
  ICreateLeadParams,
} from '@clickersphere/core/services/LeadService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { LeadQueryData, LEADS_QUERY_KEY } from './useLeads';

export function useCreateLead() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: createLeadIsPending, mutateAsync: createLeadAsync } =
    useMutation({
      mutationFn: async (data: ICreateLeadParams) => {
        const lead = await LeadService.create(data);

        return lead as LeadQueryData;
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        const tempId = String(crypto.randomUUID());

        queryClient.setQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
          (old) => [
            ...(old ?? []).concat({
              ...variables,
              id: tempId,
              active: true,
              createdAt: Date.now(),
              queryStatus: 'pending',
            }),
          ],
        );

        return { tempId };
      },

      onSuccess: async (data, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        queryClient.setQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
          (old) =>
            old?.map((lead) => (lead.id === context.tempId ? data : lead)),
        );
      },

      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        queryClient.setQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
          (old) => old?.filter((lead) => lead.id !== context?.tempId),
        );
      },
    });

  return {
    createLeadAsync,
    createLeadIsPending,
  };
}
